import type { RefObject } from 'react';

import {
  usePageConfig,
  useTabContentConfig,
} from '../../../components/PageConfigProvider/PageConfigProvider';
import { useFilterContext } from '../../contexts/FilterProvider';
import { generatePageKey } from '../../engine/filterGroupConsistency';
import type {
  PrimaryFilterId,
  TreeFilter,
  TreeSelection,
} from '../../types/filters';
import { TreeFilterForm } from './TreeFilterForm';

type PrimaryFilterFormRenderProps = {
  primaryFilter: TreeFilter | null;
  close: () => void;
  selectedFilterId: PrimaryFilterId;
  isOpen?: boolean;
  searchInputRef?: RefObject<HTMLInputElement>;
  isSelfSignupUser?: boolean;
  selectionChange?: () => void;
  disallowMultiLevelSelect?: boolean;
  groupKeyOverride?: string;
  selectionLimit?: number;
  disabledLevels?: number[];
  showSelectedValues?: boolean;
  currentTreeSelections?: TreeSelection[];
  replaceSingleSelections?: boolean;
  allowMultiLevelSelect?: boolean;
};

export const PrimaryFilterFormRender = ({
  primaryFilter,
  close,
  isOpen,
  searchInputRef,
  isSelfSignupUser,
  selectionChange,
  disallowMultiLevelSelect = false,
  groupKeyOverride,
  selectionLimit: selectionLimitProp,
  disabledLevels,
  showSelectedValues = false,
  currentTreeSelections,
  replaceSingleSelections = false,
  allowMultiLevelSelect,
}: PrimaryFilterFormRenderProps) => {
  const { dispatch: filterDispatch, filterState } = useFilterContext();
  const { config: pageConfig } = usePageConfig();
  const { revelioPageId, primaryFilter: primaryFilterConfig } =
    useTabContentConfig();

  const selectionLimit =
    selectionLimitProp ?? primaryFilterConfig?.selectionLimit ?? 6;

  const handleSavePrimaryTreeFilter = (updatedFilter: TreeFilter) => {
    if (!filterState.activeKey) {
      throw new Error('Can not save primary filter: No active key');
    }

    const pageKey = generatePageKey(
      filterState.activeKey,
      revelioPageId ?? pageConfig.id
    );

    filterDispatch({
      type: 'UPDATE_PRIMARY_SELECTIONS',
      payload: {
        filterId: updatedFilter.id,
        newSelections: updatedFilter.selections,
        pageKey,
        limit: selectionLimit,
        groupKeyOverride,
      },
    });
  };

  if (!primaryFilter) return null;
  return (
    <TreeFilterForm
      filter={primaryFilter || null}
      disallowMultiLevelSelect={
        allowMultiLevelSelect ? false : disallowMultiLevelSelect
      }
      closeMenu={close}
      isOpen={isOpen}
      searchInputRef={searchInputRef}
      isSelfSignupUser={isSelfSignupUser}
      selectionChange={selectionChange}
      onSave={handleSavePrimaryTreeFilter}
      disabledLevels={disabledLevels}
      maxSelections={selectionLimitProp}
      showSelectedValues={showSelectedValues}
      currentTreeSelections={currentTreeSelections}
      replaceSingleSelections={replaceSingleSelections}
    />
  );
};
